import { useState, type ComponentType } from 'react';
import { Avatar, type AvatarProps } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import {
  AcuraIcon,
  AlfaRomeoIcon,
  AstonMartinIcon,
  AudiIcon,
  BMWIcon,
  BYDIcon,
  BentleyIcon,
  BugattiIcon,
  BuickIcon,
  CadillacIcon,
  ChevroletIcon,
  ChryslerIcon,
  DodgeIcon,
  FerrariIcon,
  FiatIcon,
  FordIcon,
  GMCIcon,
  GenesisIcon,
  HondaIcon,
  HummerIcon,
  HyundaiIcon,
  InfinitiIcon,
  JaguarIcon,
  JeepIcon,
  KiaIcon,
  KoenigseggIcon,
  LamborghiniIcon,
  LandroverIcon,
  LexusIcon,
  LincolnIcon,
  LotusIcon,
  LucidIcon,
  MBIcon,
  MaseratiIcon,
  MazdaIcon,
  MclarenIcon,
  MiniIcon,
  MitsubishiIcon,
  NissanIcon,
  PaganiIcon,
  PolestarIcon,
  PorscheIcon,
  RAMIcon,
  RivianIcon,
  RollsRoyceIcon,
  SubaruIcon,
  TeslaIcon,
  ToyotaIcon,
  VinfastIcon,
  VolkswagenIcon,
  VolvoIcon,
} from '@cardog-icons/react';

type CardogIconComponent = ComponentType<{ width?: number | string; height?: number | string }>;

/** Slug → cardog Icon component (symbol only, no text). */
const CARDOG_MAP: Record<string, CardogIconComponent> = {
  'acura': AcuraIcon,
  'alfa-romeo': AlfaRomeoIcon,
  'aston-martin': AstonMartinIcon,
  'audi': AudiIcon,
  'bentley': BentleyIcon,
  'bmw': BMWIcon,
  'bugatti': BugattiIcon,
  'buick': BuickIcon,
  'byd': BYDIcon,
  'cadillac': CadillacIcon,
  'chevrolet': ChevroletIcon,
  'chrysler': ChryslerIcon,
  'dodge': DodgeIcon,
  'ferrari': FerrariIcon,
  'fiat': FiatIcon,
  'ford': FordIcon,
  'genesis': GenesisIcon,
  'gmc': GMCIcon,
  'honda': HondaIcon,
  'hummer': HummerIcon,
  'hyundai': HyundaiIcon,
  'infiniti': InfinitiIcon,
  'jaguar': JaguarIcon,
  'jeep': JeepIcon,
  'kia': KiaIcon,
  'koenigsegg': KoenigseggIcon,
  'lamborghini': LamborghiniIcon,
  'land-rover': LandroverIcon,
  'lexus': LexusIcon,
  'lincoln': LincolnIcon,
  'lotus': LotusIcon,
  'lucid': LucidIcon,
  'maserati': MaseratiIcon,
  'mazda': MazdaIcon,
  'mclaren': MclarenIcon,
  'mercedes-benz': MBIcon,
  'mini': MiniIcon,
  'mitsubishi': MitsubishiIcon,
  'nissan': NissanIcon,
  'pagani': PaganiIcon,
  'polestar': PolestarIcon,
  'porsche': PorscheIcon,
  'ram': RAMIcon,
  'rivian': RivianIcon,
  'rolls-royce': RollsRoyceIcon,
  'subaru': SubaruIcon,
  'tesla': TeslaIcon,
  'toyota': ToyotaIcon,
  'vinfast': VinfastIcon,
  'volkswagen': VolkswagenIcon,
  'volvo': VolvoIcon,
};

/** Avto-dev CDN base URL for PNG logo fallback. */
const AVTO_CDN_BASE = 'https://vl.imgix.net/img';

/** Brand background colors — light pastel tones that complement the logo. */
const BRAND_COLORS: Record<string, string> = {
  // Japanese
  toyota: '#e8d5d5',
  honda: '#dcd4d4',
  nissan: '#dcd4d4',
  mazda: '#dcd4d4',
  mitsubishi: '#dcd4d4',
  subaru: '#d4dde8',
  suzuki: '#dcd4d4',
  daihatsu: '#d4e8d8',
  lexus: '#dcdcdc',
  infiniti: '#dcdcdc',
  isuzu: '#dcd4d4',
  // German
  bmw: '#d4e2f0',
  'mercedes-benz': '#dcdcdc',
  'mercedes-amg': '#dcdcdc',
  volkswagen: '#d4dde8',
  audi: '#dcdcdc',
  porsche: '#dcdcdc',
  opel: '#e8e4d4',
  smart: '#dcdcdc',
  mini: '#dcdcdc',
  // French
  peugeot: '#d4dde8',
  citroen: '#dcd8d4',
  renault: '#e8e4d4',
  ds: '#d4d4dc',
  alpine: '#d4dde8',
  // Italian
  fiat: '#dcd4d4',
  'alfa-romeo': '#dcd4d4',
  ferrari: '#e8d5d5',
  lamborghini: '#e8e4d4',
  maserati: '#d4dde8',
  lancia: '#d4dde8',
  abarth: '#e8d5d5',
  // Swedish
  volvo: '#d4dde8',
  saab: '#d4e8d8',
  polestar: '#dcdcdc',
  // Czech / Spanish
  skoda: '#d4e8d8',
  seat: '#dcd4d4',
  cupra: '#d4d4dc',
  // Korean
  hyundai: '#d4dde8',
  kia: '#dcd4d4',
  genesis: '#d4d4dc',
  ssangyong: '#dcd4d4',
  // American
  ford: '#d4dde8',
  chevrolet: '#e8e4d4',
  dodge: '#dcd4d4',
  jeep: '#d4e8d8',
  chrysler: '#d4dde8',
  cadillac: '#dcdcdc',
  lincoln: '#dcdcdc',
  buick: '#dcdcdc',
  gmc: '#dcd4d4',
  ram: '#dcd4d4',
  tesla: '#dcd4d4',
  rivian: '#d4e8d8',
  lucid: '#dcdcdc',
  // British
  'land-rover': '#d4e2d4',
  jaguar: '#d4e2d4',
  bentley: '#d4e2d4',
  'rolls-royce': '#dcdcdc',
  'aston-martin': '#d4e8d8',
  lotus: '#e8e4d4',
  morgan: '#dcd8d4',
  mclaren: '#e8d5d5',
  // Romanian
  dacia: '#d4dde8',
  // Chinese
  byd: '#dcd4d4',
  mg: '#dcd4d4',
  nio: '#d4dde8',
  xpeng: '#dcdcdc',
  geely: '#d4dde8',
  'lynk-and-co': '#d4dde8',
  // Other
  'bmw-m': '#d4e2f0',
  'audi-sport': '#dcdcdc',
  vinfast: '#d4dde8',
};

/** Soft pastel palette for brands not in the map above. */
const FALLBACK_COLORS = [
  '#e0d8d0', '#d4dde8', '#dcd8d4', '#d8dce0',
  '#e0dcd4', '#d4d8dc', '#dcd4dc', '#d8e0d4',
];

function makeToSlug(make: string): string {
  return make
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-');
}

function getBrandBgColor(slug: string): string {
  const brand = BRAND_COLORS[slug];
  if (brand) return brand;
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash);
  }
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length] ?? '#dcdcdc';
}

interface VehicleMakeLogoProps extends Omit<AvatarProps, 'children' | 'src'> {
  make: string | null | undefined;
  size?: number;
}

export function VehicleMakeLogo({ make, size = 40, sx, ...rest }: VehicleMakeLogoProps) {
  const [imgError, setImgError] = useState(false);
  const slug = make ? makeToSlug(make) : null;
  const bgcolor = slug ? getBrandBgColor(slug) : 'primary.light';

  const CardogIcon = slug ? CARDOG_MAP[slug] : undefined;

  // Priority 1: Cardog SVG icon (symbol only)
  if (CardogIcon) {
    return (
      <Avatar
        variant="rounded"
        sx={{ width: size, height: size, bgcolor, borderRadius: '20%', ...sx }}
        {...rest}
      >
        <CardogIcon width={size} height={size} />
      </Avatar>
    );
  }

  // Priority 2: avto-dev CDN PNG fallback
  const showPng = slug && !imgError;

  return (
    <Avatar
      variant="rounded"
      src={showPng ? `${AVTO_CDN_BASE}/${slug}-logo.png` : undefined}
      imgProps={showPng ? { onError: () => setImgError(true), style: { objectFit: 'contain' } } : undefined}
      sx={{
        width: size,
        height: size,
        borderRadius: '20%',
        bgcolor,
        ...sx,
      }}
      {...rest}
    >
      {/* Priority 3: generic car icon */}
      <DirectionsCarIcon sx={{ fontSize: size * 0.55 }} />
    </Avatar>
  );
}
